import { ChartDomain, ChartTypes, OrgTypes, OrganizationTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT, deepCopy, safeStringToNumber } from '@mode/shared/util-js';

import { Encoding } from '@mode-vegas/vegas';
import { EncodingHelpers, RenderDataModel } from '@mode/charts/util-vegas';
import { FlatTableColorScheme } from '@mode/shared/util-js';
import produce from 'immer';
import * as ChartSwitchActions from './chart/chart-switch.actions';

const MAX_UNDOS = 100;

export function tokenFromVizID(id: string) {
  return id.split('_')[1] || id;
}

export const SORT_ORDER_ASC = 'asc';
export const SORT_ORDER_DESC = 'desc';

function buildChartFormat(format: Readonly<ChartTypes.ChartFormat>) {
  if (!format) {
    return deepCopy(DEFAULT_CHART_FORMAT);
  }

  return produce(format, (draft) => {
    if (draft.drilldownEnabled === undefined) {
      draft.drilldownEnabled = true;
    }

    // TODO: remove soft cast after typescript 4.0 upgrade
    (['xAxis', 'yAxis', 'y2Axis'] as Array<'xAxis' | 'yAxis' | 'y2Axis'>).forEach((axisName) => {
      const axis = draft[axisName];
      if (axis) {
        const numberRange = axis.numberRange;
        if (numberRange) {
          numberRange.min = safeStringToNumber(numberRange.min, null);
          numberRange.max = safeStringToNumber(numberRange.max, null);
        }
      }
    });

    if (draft.flatTable?.general?.colorScheme) {
      draft.flatTable.general.colorScheme = FlatTableColorScheme.migrateRebrand2022(
        draft.flatTable.general.colorScheme
      );
    }
  });
}

export function loadChartFormat(chart: Readonly<ChartTypes.ChartResource>): ChartTypes.ChartFormat {
  const format = chart.view_vegas && chart.view_vegas.format;
  return buildChartFormat(format);
}

export function loadChartFormatFromMappedChart(chart: ChartDomain.ChartWithQueryToken) {
  const format = chart.format;
  return buildChartFormat(format);
}

export function makeChartHistoryStack(resource: ChartTypes.ChartResource): ChartTypes.ChartHistory {
  return {
    undoStack: [],
    last: {
      chartType: resource.view_vegas.chartType,
      encoding: EncodingHelpers.normalizeEncoding(resource.view_vegas.encoding),
      fieldFormats: resource.view_vegas.fieldFormats,
      fieldColors: resource.view_vegas.fieldColors,
      facetSizes: resource.view_vegas.facetSizes,
      format: resource.view_vegas.format,
      title: resource.view_vegas.title,
      description: resource.view_vegas.description,
      caption: resource.view_vegas.caption,
      colorPaletteToken: resource.color_palette_token,
      isAutoUpdateDisabled: resource.view_vegas.isAutoUpdateDisabled ?? false,
    },
    redoStack: [],
  };
}

export function makeChartHistoryStackFromMappedChart(
  chart: ChartDomain.Chart | ChartDomain.ChartWithQueryToken
): ChartTypes.ChartHistory {
  return {
    undoStack: [],
    last: {
      chartType: chart.chartType,
      encoding: EncodingHelpers.normalizeEncoding(chart.encoding),
      fieldFormats: chart.fieldFormats,
      fieldColors: chart.fieldColors,
      facetSizes: chart.facetSizes,
      format: chart.format,
      title: chart.title || '',
      description: chart.description || '',
      caption: chart.caption,
      colorPaletteToken: chart.colorPaletteToken,
      isAutoUpdateDisabled: chart.isAutoUpdateDisabled ?? false,
    },
    redoStack: [],
  };
}

export function popUndoStack(history: ChartTypes.ChartHistory | undefined): ChartTypes.ChartHistory {
  if (history == null) {
    throw new Error('popUndoStack: history is required');
  }

  const prev = history.undoStack[0];
  if (prev) {
    return {
      undoStack: [...history.undoStack.slice(1, MAX_UNDOS)],
      last: prev.state,
      redoStack: [{ action: prev.action, state: history.last }, ...history.redoStack],
    };
  }

  return history;
}

export function popRedoStack(history: ChartTypes.ChartHistory | undefined): ChartTypes.ChartHistory {
  if (history == null) {
    throw new Error('popRedoStack: history is required');
  }

  const next = history.redoStack[0];

  if (next) {
    return {
      undoStack: [{ action: next.action, state: history.last }, ...history.undoStack],
      last: next.state,
      redoStack: history.redoStack.slice(1, MAX_UNDOS),
    };
  }

  return history;
}

/**
 * Pushes the current chart state into the undo stack and returns an updated state with the stack
 * @param {ChartState} chart
 * @param {string} action
 * @returns {ChartState}
 */
export function pushUndoStack(
  history: ChartTypes.ChartHistory,
  next: Partial<ChartTypes.ChartSnapshot>,
  action: string
): ChartTypes.ChartHistory {
  let undos = history.undoStack;
  undos = undos.slice(0, MAX_UNDOS);

  return {
    undoStack: [{ action: action, state: history.last }, ...undos],
    last: { ...history.last, ...next },
    redoStack: [],
  };
}

/**
 * Updates the `last` parameter with values in the provided ChartSnapshot. This is used to
 * replace the undo item on actions conducted automatically, e.g. backfills.
 */
export function replaceLastHistory(
  history: ChartTypes.ChartHistory | undefined,
  next: Partial<ChartTypes.ChartSnapshot>
): ChartTypes.ChartHistory {
  if (history == null) {
    throw new Error('replaceLastHistory: history is required');
  }

  return {
    ...history,
    last: {
      ...history.last,
      ...next,
    },
  };
}

export function isColorPaletteCompatible(vizType: ChartTypes.ChartType, palette: OrgTypes.ColorPalette | undefined) {
  switch (vizType) {
    case 'bigValue':
      return palette?.type === 'divergent';
    default:
      return palette?.type === 'categorical';
  }
}

export function getDefaultColorPaletteForType(
  vizType: ChartTypes.ChartType,
  defaultPalettes: OrganizationTypes.DefaultColorPaletteTokens
) {
  switch (vizType) {
    case 'bigValue':
      return defaultPalettes.divergent;
    default:
      return defaultPalettes.categorical;
  }
}

export function peekUndoStack(history?: ChartTypes.ChartHistory): ChartTypes.ChartSnapshot | undefined {
  return history?.undoStack[0]?.state;
}

export function peekRedoStack(history?: ChartTypes.ChartHistory): ChartTypes.ChartSnapshot | undefined {
  return history?.redoStack[0]?.state;
}

export function generateColorMapping(renderModel: RenderDataModel, encoding: Encoding, format: ChartTypes.ChartFormat) {
  // Ensure that the ExprFrame is set on all EncFields so that we generate the
  // proper series names.
  const encodingWithExprFrames = EncodingHelpers.setMissingAnalyticFrames(encoding, renderModel);
  const seriesNames = renderModel.getColorDomain(encodingWithExprFrames, format);
  const colorMap: { [seriesName: string]: string } = {};
  const colors = format.colors.custom; // note that it's not necessary to call
  seriesNames.some((seriesName, i) => {
    if (i >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT) {
      return true;
    }

    colorMap[seriesName ?? 'null'] = colors[i % colors.length];
    return false;
  });

  return colorMap;
}

/**
 * Determines if the color field changed between the old encoding and the
 * new encoding.
 */
export function isQuickChartColorChanged(oldEncoding: Encoding, newEncoding: Encoding) {
  const getColorFields = (encoding: Encoding) => {
    if (encoding.marks.channel === '*') {
      return [encoding.marks.properties.color.values[0]];
    }
    return encoding.marks.series.map((series) => {
      return series.properties.color.values[0];
    });
  };
  const oldColorFields = getColorFields(oldEncoding);
  const newColorFields = getColorFields(newEncoding);

  return (
    oldColorFields.length !== newColorFields.length ||
    oldColorFields.some((oldField, index) => oldField?.formula !== newColorFields[index]?.formula)
  );
}

// This is a list of Actions which will modify the chart such that, in Manual Update mode, we will
// mark the visualization as 'dirty' (ie needs to be updated).
export const updateEncodingActions = [
  ChartSwitchActions.updateDesignerChartType,
  ChartSwitchActions.updateDesignerChartEncoding,
  ChartSwitchActions.updateEncodingAndFormat,
  ChartSwitchActions.swapAxes,
  ChartSwitchActions.removeSort,
  ChartSwitchActions.quickSort,
  ChartSwitchActions.clearChart,
  ChartSwitchActions.clearFilters,
  ChartSwitchActions.clearSorts,
];

// This is an additional list of Actions which will modify the chart. However, these will not trigger
// the initial 'dirty' state. They only count for 'unapplied changes' after one of the above Actions
// has triggered the 'dirty' state.
export const updateFormatOrColorActions = [
  ChartSwitchActions.clearFormatting,
  ChartSwitchActions.updateAxisFieldFormat,
  ChartSwitchActions.updateDesignerFieldColor,
  ChartSwitchActions.updateFormat,
  ChartSwitchActions.updateFieldFormat,
  ChartSwitchActions.updateHeaderFieldFormat,
  ChartSwitchActions.updateLegendFieldFormat,
  ChartSwitchActions.updatePaneFieldFormat,
];

export function isUpdateChartAction(action: string | undefined) {
  return action !== undefined
    ? [...updateEncodingActions, ...updateFormatOrColorActions].some((change) => change.type === action)
    : false;
}

export function isUpdateFormatOrColorAction(action: string | undefined) {
  return action !== undefined ? updateFormatOrColorActions.some((change) => change.type === action) : false;
}
